/**
 * Escritura de etiquetas SEO en el <head> (única parte que toca el DOM).
 *
 * Toda etiqueta gestionada lleva `data-i18n-seo="<clave>"`. Así:
 *  - se reutiliza (no se duplica) si ya existe, incluidas las estáticas de
 *    `public/index.html`, que llevan el mismo marcador;
 *  - se eliminan las que dejan de hacer falta (p. ej. `og:locale:alternate`
 *    del idioma que ahora es el actual).
 */

const MARK = 'data-i18n-seo';

/**
 * Crea o actualiza la etiqueta con esa clave.
 * @param {string} key - Identificador único dentro del marcador.
 * @param {string} tag - `meta` o `link`.
 * @param {Object<string,string>} attrs - Atributos a fijar.
 */
const upsert = (key, tag, attrs) => {
  let el = document.head.querySelector(`[${MARK}="${key}"]`);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(MARK, key);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([name, value]) => el.setAttribute(name, value));
};

/**
 * Aplica el conjunto completo de etiquetas SEO de una página.
 *
 * @param {Object} seo
 * @param {string} seo.title - Título del documento.
 * @param {string} seo.description - Meta descripción.
 * @param {string} seo.canonical - URL canónica.
 * @param {Array<{hreflang: string, href: string}>} seo.alternates - Alternates hreflang.
 * @param {string} seo.ogLocale - `og:locale` actual.
 * @param {string[]} seo.ogLocaleAlternates - `og:locale:alternate` (resto de idiomas).
 */
export const applyHeadTags = ({
  title,
  description,
  canonical,
  alternates,
  ogLocale,
  ogLocaleAlternates,
}) => {
  const wanted = new Set();
  const set = (key, tag, attrs) => {
    wanted.add(key);
    upsert(key, tag, attrs);
  };
  const named = (name, content) => set(name, 'meta', { name, content });
  const prop = (property, content) => set(property, 'meta', { property, content });

  document.title = title;
  named('description', description);
  set('canonical', 'link', { rel: 'canonical', href: canonical });
  alternates.forEach(({ hreflang, href }) =>
    set(`alt-${hreflang}`, 'link', { rel: 'alternate', hreflang, href })
  );

  prop('og:title', title);
  prop('og:description', description);
  prop('og:url', canonical);
  prop('og:locale', ogLocale);
  ogLocaleAlternates.forEach((locale) =>
    set(`og:locale:alternate:${locale}`, 'meta', { property: 'og:locale:alternate', content: locale })
  );

  named('twitter:title', title);
  named('twitter:description', description);
  named('twitter:url', canonical);

  document.head.querySelectorAll(`[${MARK}]`).forEach((el) => {
    if (!wanted.has(el.getAttribute(MARK))) el.remove();
  });
};
